import * as PIXI from 'pixi.js';

import { APP_HEIGHT, APP_WIDTH } from '../../features/utils';
import { bindingsFor, clipCreate } from '../../features/animations/prefab';
import { customPropertyIds } from '../../features/animations/registry';
import { applyClipPose, captureRest } from '../../features/animations/player';

export default class AnimationPreviewStage extends PIXI.Container {
    constructor() {
        super();
        this.label = 'animationPreviewStage';
        this.prefab = null;
        this.root = null;
        this.transformRest = {};
        this.mountToken = 0;
        this.screenWidth = APP_WIDTH;
        this.screenHeight = APP_HEIGHT;

        this.backdrop = new PIXI.Graphics();
        this.backdrop.label = 'animationPreviewBackdrop';
        this.addChild(this.backdrop);

        this.world = new PIXI.Container();
        this.world.label = 'animationPreviewWorld';
        this.addChild(this.world);
        this.resize(APP_WIDTH, APP_HEIGHT);
    }

    async attach(app) {
        this.resize(app.screen.width, app.screen.height);
        app.stage.addChild(this);
    }

    detach(app) {
        if (this.released) return;
        this.released = true;
        this.mountToken += 1;
        if (this.parent) app.stage.removeChild(this);
        this.destroy({ children: true });
    }

    resize(width, height) {
        if (this.released) return;
        this.screenWidth = width;
        this.screenHeight = height;
        this.backdrop.clear();
        this.backdrop.rect(0, 0, width, height);
        this.backdrop.fill({ color: 0x12141c });
        this.fitRoot();
    }

    async mountPrefab(prefab, clipName) {
        const token = this.mountToken + 1;
        this.mountToken = token;
        if (!prefab || this.released) return;

        const create = clipCreate(prefab, clipName);
        if (this.prefab?.id === prefab.id && this.create === create && this.root) return;

        await prefab.load();
        if (this.released || this.mountToken !== token) return;

        if (this.root) {
            this.world.removeChild(this.root);
            this.root.destroy({ children: true });
        }

        this.prefab = prefab;
        this.create = create;
        this.root = create();
        this.world.addChild(this.root);
        this.captureTransformRest();
        this.fitRoot();
    }

    captureTransformRest() {
        this.transformRest = {};
        Object.entries(bindingsFor(this.prefab, this.root)).forEach(([id, binding]) => {
            const target = binding.target;
            this.transformRest[id] = {
                x: target.x,
                y: target.y,
                scaleX: target.scale.x,
                scaleY: target.scale.y,
                rotation: target.rotation,
                alpha: target.alpha,
                visible: target.visible,
            };
        });
    }

    resetTransforms() {
        Object.entries(this.transformRest).forEach(([id, rest]) => {
            const part = this.prefab?.parts?.[id];
            const target = part?.pick?.(this.root);
            if (!target) return;
            target.position.set(rest.x, rest.y);
            target.scale.set(rest.scaleX, rest.scaleY);
            target.rotation = rest.rotation;
            target.alpha = rest.alpha;
            target.visible = rest.visible;
            if (typeof target.rotate === 'function') {
                target.rotate(0, 0, part.perspective ?? 900);
            }
        });
    }

    fitRoot() {
        if (!this.root) return;
        const bounds = this.root.getLocalBounds();
        const width = bounds.width || this.screenWidth;
        const height = bounds.height || this.screenHeight;
        const availableW = Math.max(this.screenWidth - 48, 1);
        const availableH = Math.max(this.screenHeight - 48, 1);
        const scale = Math.min(availableW / width, availableH / height);
        this.world.scale.set(scale);
        this.world.position.set(
            (this.screenWidth - width * scale) / 2 - bounds.x * scale,
            (this.screenHeight - height * scale) / 2 - bounds.y * scale,
        );
    }

    bindings() {
        return bindingsFor(this.prefab, this.root);
    }

    rests(clip) {
        const rests = {};
        Object.entries(this.bindings()).forEach(([id, binding]) => {
            rests[id] = captureRest(binding.target, customPropertyIds(clip, id), binding, id);
        });
        return rests;
    }

    prepare() {
        this.resetTransforms();
        return this.bindings();
    }

    show(clip, time) {
        if (!this.root || !clip) return;
        this.resetTransforms();
        applyClipPose(clip, time, this.bindings(), this.rests(clip));
    }
}
